import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faUserTag, faCrown } from "@fortawesome/free-solid-svg-icons";

const ROLE_OPTIONS = [
  {
    value: "super_admin",
    label: "Super Admin",
    desc: "Complete system control — all modules, settings, and user management",
    isSuper: true,
  },
  {
    value: "admin",
    label: "Admin",
    desc: "Full system access — all modules and settings",
  },
  {
    value: "owner",
    label: "Owner",
    desc: "Manage users, reports, products, and stock",
  },
  {
    value: "seller",
    label: "Seller",
    desc: "Record sales and view assigned products",
  },
];

const getInitialRole = (userRole) => {
  const normalized = (userRole || "").toLowerCase();
  const match = ROLE_OPTIONS.find((r) => r.value === normalized);
  return match ? match.value : "seller";
};

const RoleTab = ({ user, onSave }) => {
  const [selectedRole, setSelectedRole] = useState(getInitialRole(user.role));

  const handleSave = () => {
    onSave({ ...user, role: selectedRole });
  };

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <div className="p-5 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faUserTag}
              className="text-purple-800 h-4 w-4"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">User role</h3>
            <p className="text-sm text-gray-600">
              Assign a role to {user.full_name}
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {ROLE_OPTIONS.map(({ value, label, desc, isSuper }) => {
            const isSelected = selectedRole === value;

            return (
              <div
                key={value}
                onClick={() => setSelectedRole(value)}
                className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? `${
                        isSuper
                          ? "border-amber-600 bg-amber-50"
                          : "border-blue-800 bg-blue-50"
                      } shadow-sm`
                    : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  {isSuper && (
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faCrown}
                        className="text-amber-600 h-4 w-4"
                      />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p
                        className={`font-medium ${
                          isSelected
                            ? isSuper
                              ? "text-amber-800"
                              : "text-blue-800"
                            : "text-gray-900"
                        }`}
                      >
                        {label}
                      </p>
                      {isSuper && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                          Highest Access
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{desc}</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="user-role"
                  checked={isSelected}
                  onChange={() => setSelectedRole(value)}
                  className={`h-4 w-4 cursor-pointer ${
                    isSuper ? "accent-amber-600" : "accent-blue-800"
                  }`}
                />
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-200">
          <Button
            onClick={handleSave}
            disabled={selectedRole === getInitialRole(user.role)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
            Save role
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default RoleTab;
